import { Platform } from '@angular/cdk/platform';

import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { TranslateModule } from '@ngx-translate/core';

type CSSStyleDeclarationWithZoom = CSSStyleDeclaration & { zoom: string };

@Component({
  selector: 'ebeihilfe-portal-image-modal',
  templateUrl: './image-modal.component.html',
  styleUrls: ['./image-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogContent,
    MatDialogActions,
    TranslateModule,
    MatSlider,
    MatSliderThumb,
    MatIconButton,
    MatIcon,
    MatButton,
  ],
})
export class ImageModalComponent implements AfterViewInit {
  private readonly dialogRef = inject(MatDialogRef<ImageModalComponent>);
  private readonly platform = inject(Platform);

  @ViewChild('canvas', { static: false }) public canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('origin', { static: false }) public origin!: ElementRef<HTMLImageElement>;

  public readonly dialogData = inject<{ imageBlob: Blob; editMode: boolean }>(MAT_DIALOG_DATA);
  public angle = 0;
  public angles = [0, 90, 180, 270];

  public ngAfterViewInit(): void {
    this.initializeImage();
  }

  public rotate(direction: 'left' | 'right'): void {
    if (direction === 'right' && this.angle === this.angles.length - 1) {
      this.angle = 0;
    } else if (direction === 'left' && this.angle === 0) {
      this.angle = this.angles.length - 1;
    } else {
      this.angle = direction === 'left' ? this.angle - 1 : this.angle + 1;
    }
    this.rotateImage();
  }

  public async close(): Promise<void> {
    const isImageRotated = this.angle !== 0;
    this.dialogRef.close({
      hasChanged: isImageRotated,
      imageBlob: isImageRotated ? await this.canvasToBlob() : null,
    });
  }

  public zoom(currentValue: number): void {
    if (this.platform.FIREFOX) {
      this.canvas.nativeElement.style.transform = 'scale(' + currentValue / 100 + ')';
    } else {
      // zoom is not a standard
      (this.canvas.nativeElement.style as CSSStyleDeclarationWithZoom).zoom = `${currentValue}%`;
    }
  }

  public zoomLabel(value: number): string {
    return `${value}%`;
  }

  private initializeImage(): void {
    this.origin.nativeElement.src = window.URL.createObjectURL(this.dialogData.imageBlob);
    this.origin.nativeElement.onload = () => this.rotateImage();
  }

  private rotateImage(): void {
    const img = this.origin.nativeElement;
    const canvas = this.canvas.nativeElement;

    const context2D = canvas.getContext('2d');
    if (!context2D) {
      return;
    }
    let cw = img.width,
      ch = img.height,
      cx = 0,
      cy = 0;

    switch (this.angles[this.angle]) {
      case 90:
        cw = img.height;
        ch = img.width;
        cy = img.height * -1;
        break;
      case 180:
        cx = img.width * -1;
        cy = img.height * -1;
        break;
      case 270:
        cw = img.height;
        ch = img.width;
        cx = img.width * -1;
        break;
    }

    canvas.setAttribute('width', cw.toString(10));
    canvas.setAttribute('height', ch.toString(10));
    context2D.rotate((this.angles[this.angle] * Math.PI) / 180);
    context2D.drawImage(img, cx, cy);
  }

  private canvasToBlob(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.canvas.nativeElement.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas conversion failed'));
          return;
        }
        resolve(blob);
      });
    });
  }
}
